import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error, FileUpload } from '@mui/icons-material';

import api from '../../../services/api';

const BudgetCodeUpload = () => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [uploadResult, setUploadResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Only allow .xls files as per documentation
      if (!file.name.toLowerCase().endsWith('.xls')) {
        setMessage({ text: 'Invalid file format. Only .xls files are allowed.', type: 'error' });
        return;
      }
      
      setSelectedFile(file);
      setMessage({ text: '', type: '' });
      setUploadResult(null);
      setErrors([]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({ text: 'Please select a file to upload', type: 'error' });
      return;
    }

    setUploading(true);
    setMessage({ text: '', type: '' });
    setErrors([]);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('rbcFile', selectedFile);

    try {
      const response = await api.post('/budget-code/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;

      if (result.success) {
        setMessage({ 
          text: `✓ Successfully uploaded ${result.count} budget code(s)`, 
          type: 'success' 
        });
        setUploadResult(result);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorData = error.response?.data;
      
      if (errorData?.errors && errorData.errors.length > 0) {
        setErrors(errorData.errors);
        setMessage({ 
          text: `✗ Upload failed: ${errorData.errors.length} validation error(s) found. Click "View Details" to see specific errors.`, 
          type: 'error' 
        });
        setShowErrors(true);
      } else {
        setMessage({ 
          text: `✗ Upload failed: ${errorData?.message || error.message}`, 
          type: 'error' 
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create template directly in frontend
    const templateData = [
      ['Bus Group', 'Profit Center', 'Budget Code', 'Accounting Contact', 'Business Name', 'Street Address', 'City, State, Zipcode'],
      ['Marketing', '1234', '123456789', 'john.doe@company.com', 'ABC Corporation', '123 Main Street', 'San Diego, CA, 92101'],
      ['Sales', '5678', '987654321', 'jane.smith@company.com', 'XYZ Industries', '456 Oak Avenue', 'Los Angeles, CA, 90001']
    ];

    const csvContent = templateData.map(row => row.join('\t')).join('\n');
    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'BudgetCodeTemplate.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Budget Code Upload</Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>File Upload</Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload an Excel file (.xls format only) containing budget code information. 
            The file must have exact headers: Bus Group | Profit Center | Budget Code | Accounting Contact | Business Name | Street Address | City, State, Zipcode.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xls"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<FileUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Select File
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadTemplate}
            >
              Download Template
            </Button>
          </Box>

          {selectedFile && (
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} KB)`}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload Budget Codes'}
          </Button>
        </CardContent>
      </Card>

      {uploadResult && (
        <Card sx={{ mb: 3, bgcolor: 'success.light' }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6" color="success.dark">
                  Upload Successful!
                </Typography>
                <Typography variant="body1">
                  Successfully processed <strong>{uploadResult.count}</strong> budget code(s)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {errors.length > 0 && (
        <Card sx={{ bgcolor: 'error.light' }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Error color="error" sx={{ fontSize: 40 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" color="error.dark" sx={{ mb: 1 }}>
                  Validation Failed - {errors.length} Error(s) Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The following rows have validation errors. Please correct them in your Excel file and upload again.
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setShowErrors(true)}
                  size="small"
                >
                  View Error Details
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog 
        open={showErrors} 
        onClose={() => setShowErrors(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          Validation Errors - {errors.length} Row(s) Failed
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Fix these errors in your Excel file and upload again. Row numbers correspond to your Excel file.
          </Alert>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Row #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Error Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>{error.row}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{error.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrors(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetCodeUpload;